/**
 * Pas tenant-branding toe op `document.documentElement` (CSS-vars) en
 * `document.head` (favicon-trio + web-app-manifest). Port van de Vue-
 * `useBranding.ts` — gedrag is identiek, alleen de `watch`-wrapper is
 * weg (in React koppelen we 'm via useEffect in App.tsx).
 *
 * Drie side-effects:
 *  - Colour fields (`primary_color`, etc.) → CSS custom properties op
 *    `:root`. Aurora-tokens derived hover/subtle blijven via cascade
 *    in sync.
 *  - `favicon_url` → managed trio van `<link>`-tags (icon +
 *    apple-touch-icon + manifest). Bij elke call worden ALLE bestaande
 *    icon-tags eerst verwijderd — voorkomt de "flicker"-bug van Vue.
 *  - Applied CSS-var-keys staan in `data-aurora-applied` zodat de
 *    volgende call ze deterministisch kan opruimen.
 */

import { AURORA_PRIMARY_HEX, BCS_PINWHEEL_DATA_URI } from '@shared/brandingDefaults';

export interface TenantBranding {
  primary_color?: string | null;
  accent_color?: string | null;
  button_hover_color?: string | null;
  active_indicator_color?: string | null;
  sidebar_color?: string | null;
  favicon_url?: string | null;
  name_in_ui?: string | null;
  logo_url?: string | null;
  logo_layout?: 'logo_only' | 'logo_then_title' | 'logo_above_title' | 'title_only';
  logo_align?: 'left' | 'center';
}

const APPLIED_DATA_ATTR = 'auroraApplied';

export interface BrandingApplyContext {
  tenantName?: string | null;
}

export function applyBranding(
  branding: TenantBranding | null | undefined,
  context: BrandingApplyContext = {},
): void {
  if (typeof document === 'undefined') return;

  applyColors(branding);
  applyFavicon(branding, context);
}

function applyColors(branding: TenantBranding | null | undefined): void {
  const root = document.documentElement;

  const previous = root.dataset[APPLIED_DATA_ATTR];
  if (previous) {
    for (const key of previous.split(',')) {
      if (key) root.style.removeProperty(key);
    }
    delete root.dataset[APPLIED_DATA_ATTR];
  }

  const applied: string[] = [];

  if (isSafeHexColor(branding?.primary_color)) {
    root.style.setProperty('--color-primary', branding!.primary_color!);
    applied.push('--color-primary');
  }

  if (isSafeHexColor(branding?.accent_color)) {
    root.style.setProperty('--color-accent', branding!.accent_color!);
    applied.push('--color-accent');
  }

  if (isSafeHexColor(branding?.button_hover_color)) {
    root.style.setProperty('--color-primary-hover', branding!.button_hover_color!);
    applied.push('--color-primary-hover');
  }

  if (isSafeHexColor(branding?.active_indicator_color)) {
    root.style.setProperty('--sidebar-active', branding!.active_indicator_color!);
    applied.push('--sidebar-active');
  }

  if (isSafeHexColor(branding?.sidebar_color)) {
    const base = branding!.sidebar_color!;
    const gradient = `linear-gradient(180deg, ${base}, color-mix(in srgb, ${base} 90%, #000))`;
    root.style.setProperty('--gradient-sidebar', gradient);
    applied.push('--gradient-sidebar');
  }

  if (applied.length > 0) {
    root.dataset[APPLIED_DATA_ATTR] = applied.join(',');
  }
}

function applyFavicon(
  branding: TenantBranding | null | undefined,
  context: BrandingApplyContext,
): void {
  const head = document.head;
  const iconSelector = [
    'link[rel="icon"]',
    'link[rel="shortcut icon"]',
    'link[rel="apple-touch-icon"]',
    'link[rel="apple-touch-icon-precomposed"]',
    'link[rel="manifest"]',
  ].join(',');
  for (const existing of head.querySelectorAll(iconSelector)) {
    existing.parentNode?.removeChild(existing);
  }

  const customHref = branding?.favicon_url?.trim();
  const iconHref = customHref || BCS_PINWHEEL_DATA_URI;
  const iconType = customHref ? guessMimeType(customHref) : 'image/svg+xml';

  const icon = document.createElement('link');
  icon.rel = 'icon';
  icon.type = iconType;
  icon.href = iconHref;
  icon.dataset.auroraFavicon = '';
  head.appendChild(icon);

  if (customHref && iconType !== 'image/svg+xml') {
    const apple = document.createElement('link');
    apple.rel = 'apple-touch-icon';
    apple.href = iconHref;
    apple.dataset.auroraFavicon = '';
    head.appendChild(apple);
  }

  const manifestLink = document.createElement('link');
  manifestLink.rel = 'manifest';
  manifestLink.href = buildManifestDataUri(branding, context, iconHref, iconType);
  manifestLink.dataset.auroraFavicon = '';
  head.appendChild(manifestLink);
}

function guessMimeType(url: string): string {
  const path = url.toLowerCase().split('?')[0].split('#')[0];
  if (path.endsWith('.svg')) return 'image/svg+xml';
  if (path.endsWith('.png')) return 'image/png';
  if (path.endsWith('.jpg') || path.endsWith('.jpeg')) return 'image/jpeg';
  if (path.endsWith('.ico')) return 'image/x-icon';
  return 'image/png';
}

function buildManifestDataUri(
  branding: TenantBranding | null | undefined,
  context: BrandingApplyContext,
  iconHref: string,
  iconType: string,
): string {
  const portalName = branding?.name_in_ui?.trim();
  const tenantName = context.tenantName?.trim();
  const name = portalName || tenantName || 'BCS Learning';

  const themeColor = isSafeHexColor(branding?.primary_color)
    ? branding!.primary_color!
    : AURORA_PRIMARY_HEX;

  const manifest = {
    name,
    short_name: name.length > 12 ? name.slice(0, 12) : name,
    icons: [
      {
        src: iconHref,
        sizes: 'any',
        type: iconType,
        purpose: 'any',
      },
    ],
    theme_color: themeColor,
    background_color: '#ffffff',
    display: 'standalone',
  };

  return (
    'data:application/manifest+json;charset=utf-8,'
    + encodeURIComponent(JSON.stringify(manifest))
  );
}

function isSafeHexColor(value: string | null | undefined): boolean {
  return typeof value === 'string' && /^#[0-9a-fA-F]{3,8}$/.test(value);
}
